/**
 * MJS Drop a Hint System
 * Custom-built for My Jewellery Shop
 * Integrates with: Wishlist, Klaviyo, Men's Secret Business flow
 * Version: 1.0.0
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  const CONFIG = {
    storeName: "My Jewellery Shop",
    storeUrl: "https://myjewelleryshop.com.au",
    storeLogo: "https://myjewelleryshop.com.au/cdn/shop/files/mjs-logo.png",
    storePhone: "(07) 5572 9009",
    storeEmail: "service@myjewelleryshop.com.au",
    brandColor: "#C9A86A",
    brandColorLight: "#F5EFE3",
    hintStorageKey: "mjs_hints_sent",
    hintAnalyticsKey: "mjs_hint_analytics",
    maxHintsPerDay: 10,
    occasions: [
      "Birthday",
      "Anniversary",
      "Engagement",
      "Valentine's Day",
      "Christmas",
      "Mother's Day",
      "Father's Day",
      "Graduation",
      "Wedding",
      "Just Because",
      "Proposal Planning",
      "Other"
    ],
    relationships: [
      "Partner",
      "Spouse",
      "Friend",
      "Parent",
      "Sibling",
      "Child",
      "Colleague",
      "Other"
    ]
  };

  // ============================================
  // HINT MANAGER
  // ============================================
  const HintManager = {
    
    // Get all sent hints from localStorage
    getHints() {
      try {
        return JSON.parse(localStorage.getItem(CONFIG.hintStorageKey) || '[]');
      } catch(e) {
        return [];
      }
    },

    // Save a hint record
    saveHint(hintData) {
      const hints = this.getHints();
      const hint = {
        id: 'hint_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        ...hintData,
        sentAt: new Date().toISOString(),
        status: 'sent',
        opened: false,
        clicked: false,
        converted: false
      };
      hints.push(hint);
      localStorage.setItem(CONFIG.hintStorageKey, JSON.stringify(hints));
      return hint;
    },

    // Check daily hint limit
    canSendHint() {
      const hints = this.getHints();
      const today = new Date().toDateString();
      const todayHints = hints.filter(h => new Date(h.sentAt).toDateString() === today);
      return todayHints.length < CONFIG.maxHintsPerDay;
    },

    // Get hint count for today
    getTodayCount() {
      const hints = this.getHints();
      const today = new Date().toDateString();
      return hints.filter(h => new Date(h.sentAt).toDateString() === today).length;
    },

    // Get analytics summary
    getAnalytics() {
      const hints = this.getHints();
      return {
        totalSent: hints.length,
        totalOpened: hints.filter(h => h.opened).length,
        totalClicked: hints.filter(h => h.clicked).length,
        totalConverted: hints.filter(h => h.converted).length,
        recentHints: hints.slice(-10).reverse()
      };
    }
  };

  // ============================================
  // KLAVIYO INTEGRATION
  // ============================================
  const KLAVIYO_PUBLIC_KEY = 'S4p327';

  const KlaviyoIntegration = {

    // Track hint sent event for the SENDER
    async trackHintSent(hintData) {
      if (typeof window._learnq !== 'undefined') {
        // Track event for the sender (currently identified user)
        window._learnq.push(['track', 'Hint Sent', {
          'Product Name': hintData.productTitle,
          'Product URL': hintData.productUrl,
          'Product Price': hintData.productPrice,
          'Product Image': hintData.productImage,
          'Recipient Name': hintData.recipientName,
          'Recipient Email': hintData.recipientEmail,
          'Sender Name': hintData.senderName,
          'Sender Email': hintData.senderEmail,
          'Occasion': hintData.occasion,
          'Relationship': hintData.relationship,
          'Personal Message': hintData.message,
          'Gift Wrapping Suggested': hintData.giftWrapping,
          'Hint ID': hintData.id,
          'Source': hintData.source || 'product_page'
        }]);

        // Identify the sender
        window._learnq.push(['identify', {
          '$email': hintData.senderEmail,
          '$first_name': hintData.senderName.split(' ')[0],
          'Hints Sent': HintManager.getHints().length,
          'Last Hint Date': new Date().toISOString(),
          'MJS Role': 'Hint Sender'
        }]);
      }

      // CRITICAL: Create/update the RECIPIENT in Klaviyo and push "Hint Received" event
      // This enables the Klaviyo flow to send the actual hint email to the recipient
      await this.pushRecipientEvent(hintData);
    },

    // Push event to Klaviyo for the RECIPIENT via Client API
    // This creates the recipient as a profile and triggers the active Drop a Hint flow
    async pushRecipientEvent(hintData) {
      const senderDisplay = hintData.anonymous ? 'Someone special' : hintData.senderName;
      const senderFirstName = (senderDisplay || '').trim().split(' ')[0] || 'Someone special';
      const recipientFirstName = (hintData.recipientName || '').trim().split(' ')[0] || '';
      const recipientLastName = (hintData.recipientName || '').trim().split(' ').slice(1).join(' ') || '';
      const specialNote = hintData.message || '';
      const eventMetricName = 'Drop a Hint Submitted';
      const ensureAbsoluteUrl = (url) => {
        if (!url) return '';
        if (/^https?:\/\//i.test(url)) return url;
        return `${CONFIG.storeUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      };
      const productList = (hintData.products && hintData.products.length ? hintData.products : [{
        title: hintData.productTitle,
        price: hintData.productPrice,
        image: hintData.productImage,
        url: hintData.productUrl,
        vendor: hintData.productVendor || ''
      }]).map((product) => ({
        title: product.title || '',
        price: product.price || '',
        image: product.image || '',
        url: ensureAbsoluteUrl(product.url || ''),
        vendor: product.vendor || ''
      }));
      const primaryProduct = productList[0] || {};

      try {
        // Step 1: Create/update recipient profile via Klaviyo Client API
        const profilePayload = {
          data: {
            type: 'profile',
            attributes: {
              email: hintData.recipientEmail,
              first_name: recipientFirstName,
              last_name: recipientLastName,
              properties: {
                'Hint Recipient': true,
                'Last Hint Received': new Date().toISOString(),
                'Hint From': senderDisplay,
                'Hint Sender Email': hintData.anonymous ? '' : (hintData.senderEmail || ''),
                'MJS Role': 'Hint Recipient'
              }
            }
          }
        };

        const profileResponse = await fetch('https://a.klaviyo.com/client/profiles/?company_id=S4p327', {
          method: 'POST',
          headers: {
            'Accept': 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            'revision': '2026-04-15'
          },
          body: JSON.stringify({
            ...profilePayload,
            data: {
              ...profilePayload.data,
              attributes: {
                ...profilePayload.data.attributes,
                _kx: undefined
              },
              meta: { identifiers: { email: hintData.recipientEmail } }
            }
          })
        });

        if (!profileResponse.ok) {
          const profileError = await profileResponse.text().catch(() => 'Unknown Klaviyo profile error');
          throw new Error(`Klaviyo profile sync failed (${profileResponse.status}): ${profileError}`);
        }

        // Step 2: Push the active Drop a Hint trigger event via Klaviyo Client API
        const eventPayload = {
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: eventMetricName } } },
              profile: { data: { type: 'profile', attributes: { email: hintData.recipientEmail } } },
              properties: {
                'Product Name': primaryProduct.title || hintData.productTitle || '',
                'Product URL': primaryProduct.url || ensureAbsoluteUrl(hintData.productUrl || ''),
                'Product Price': primaryProduct.price || hintData.productPrice || '',
                'Product Image': primaryProduct.image || hintData.productImage || '',
                'Recipient Name': hintData.recipientName || '',
                'Recipient Email': hintData.recipientEmail || '',
                'Sender Name': senderDisplay,
                'Sender First Name': senderFirstName,
                'Sender Email': hintData.anonymous ? '' : (hintData.senderEmail || ''),
                'Occasion': hintData.occasion || '',
                'Relationship': hintData.relationship || '',
                'Personal Message': specialNote,
                'Special Note': specialNote,
                'Gift Wrapping Suggested': hintData.giftWrapping,
                'Hint ID': hintData.id,
                'Is Multi Hint': hintData.isMultiHint || false,
                'Number of Products': productList.length,
                'Products': productList.map(p => ({
                  ProductName: p.title,
                  ProductURL: p.url,
                  ProductPrice: p.price,
                  ImageURL: p.image,
                  Vendor: p.vendor || ''
                })),
                'Store Name': CONFIG.storeName,
                'Store URL': CONFIG.storeUrl,
                'Store Phone': CONFIG.storePhone,
                hint_recipient_name: hintData.recipientName || '',
                hint_recipient_email: hintData.recipientEmail || '',
                sender_name: senderDisplay,
                sender_first_name: senderFirstName,
                sender_email: hintData.anonymous ? '' : (hintData.senderEmail || ''),
                product_name: primaryProduct.title || hintData.productTitle || '',
                product_title: primaryProduct.title || hintData.productTitle || '',
                product_url: primaryProduct.url || ensureAbsoluteUrl(hintData.productUrl || ''),
                product_price: primaryProduct.price || hintData.productPrice || '',
                product_image: primaryProduct.image || hintData.productImage || '',
                special_note: specialNote,
                personal_message: specialNote,
                occasion: hintData.occasion || '',
                relationship: hintData.relationship || '',
                gift_wrapping_suggested: !!hintData.giftWrapping,
                hint_id: hintData.id || '',
                is_multi_hint: !!hintData.isMultiHint,
                number_of_products: productList.length,
                source: hintData.source || 'product_page',
                store_name: CONFIG.storeName,
                store_url: CONFIG.storeUrl,
                store_phone: CONFIG.storePhone
              },
              time: new Date().toISOString()
            }
          }
        };

        const eventResponse = await fetch('https://a.klaviyo.com/client/events/?company_id=S4p327', {
          method: 'POST',
          headers: {
            'Accept': 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            'revision': '2026-04-15'
          },
          body: JSON.stringify(eventPayload)
        });

        if (!eventResponse.ok) {
          const eventError = await eventResponse.text().catch(() => 'Unknown Klaviyo event error');
          throw new Error(`Klaviyo event push failed (${eventResponse.status}): ${eventError}`);
        }

        console.log('[MJS Hint] Recipient event pushed to Klaviyo:', hintData.recipientEmail);

      } catch(err) {
        console.warn('[MJS Hint] Klaviyo recipient push failed:', err);
        // Non-blocking: hint still sent via contact form even if Klaviyo fails
      }
    },

    // Track when hint modal is opened
    trackHintModalOpened(productData) {
      if (typeof window._learnq !== 'undefined') {
        window._learnq.push(['track', 'Hint Modal Opened', {
          'Product Name': productData.title,
          'Product URL': productData.url,
          'Product Price': productData.price,
          'Source': productData.source || 'product_page'
        }]);
      }
    },

    // Track hint from wishlist
    trackWishlistHint(items) {
      if (typeof window._learnq !== 'undefined') {
        window._learnq.push(['track', 'Wishlist Hint Sent', {
          'Number of Items': items.length,
          'Total Value': items.reduce((sum, item) => sum + parseFloat(item.price || 0), 0),
          'Items': items.map(i => i.title).join(', ')
        }]);
      }
    }
  };

  // ============================================
  // MODAL UI
  // ============================================
  const HintModal = {

    currentProduct: null,
    currentProducts: [], // For multi-product hints from wishlist
    isMultiHint: false,

    // Open hint modal for a single product
    open(productData) {
      this.currentProduct = productData;
      this.currentProducts = [productData];
      this.isMultiHint = false;
      this.render();
      this.show();
      KlaviyoIntegration.trackHintModalOpened(productData);
    },

    // Open hint modal for multiple products (from wishlist)
    openMulti(products) {
      this.currentProduct = products[0];
      this.currentProducts = products;
      this.isMultiHint = true;
      this.render();
      this.show();
    },

    // Render the modal HTML
    render() {
      // Remove existing modal if any
      const existing = document.getElementById('mjs-hint-modal');
      if (existing) existing.remove();

      const productPreview = this.isMultiHint 
        ? this.renderMultiProductPreview()
        : this.renderSingleProductPreview();

      const modal = document.createElement('div');
      modal.id = 'mjs-hint-modal';
      modal.className = 'mjs-hint-overlay';
      modal.innerHTML = `
        <div class="mjs-hint-modal-inner">
          <button class="mjs-hint-close" aria-label="Close">&times;</button>
          
          <div class="mjs-hint-layout">
            <!-- Left Side: Product Preview -->
            <div class="mjs-hint-left">
              <div class="mjs-hint-brand">
                <span class="mjs-hint-brand-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A86A" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </span>
                <span>DROP A HINT</span>
              </div>
              <h2 class="mjs-hint-title">Let Someone Special Know</h2>
              <p class="mjs-hint-subtitle">Send a beautifully crafted hint to someone you love. They'll receive a personalised email with your selected piece${this.isMultiHint ? 's' : ''}.</p>
              
              ${productPreview}

              <div class="mjs-hint-benefits">
                <div class="mjs-hint-benefit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A86A" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Beautifully designed hint card</span>
                </div>
                <div class="mjs-hint-benefit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A86A" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Your personal message included</span>
                </div>
                <div class="mjs-hint-benefit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A86A" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Direct link to purchase</span>
                </div>
                <div class="mjs-hint-benefit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A86A" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>100% free, no obligation</span>
                </div>
              </div>
            </div>

            <!-- Right Side: Form -->
            <div class="mjs-hint-right">
              <form id="mjs-hint-form" class="mjs-hint-form">
                <h3 class="mjs-hint-form-title">Send Your Hint</h3>
                
                <div class="mjs-hint-form-section">
                  <label class="mjs-hint-form-label">Who is this hint for?</label>
                  <div class="mjs-hint-form-row">
                    <div class="mjs-hint-form-field">
                      <input type="text" name="recipientName" placeholder="Recipient's name" required class="mjs-hint-input">
                    </div>
                    <div class="mjs-hint-form-field">
                      <input type="email" name="recipientEmail" placeholder="Recipient's email" required class="mjs-hint-input">
                    </div>
                  </div>
                </div>

                <div class="mjs-hint-form-section">
                  <label class="mjs-hint-form-label">About you</label>
                  <div class="mjs-hint-form-row">
                    <div class="mjs-hint-form-field">
                      <input type="text" name="senderName" placeholder="Your name" required class="mjs-hint-input">
                    </div>
                    <div class="mjs-hint-form-field">
                      <input type="email" name="senderEmail" placeholder="Your email" required class="mjs-hint-input">
                    </div>
                  </div>
                </div>

                <div class="mjs-hint-form-section">
                  <div class="mjs-hint-form-row">
                    <div class="mjs-hint-form-field">
                      <label class="mjs-hint-form-label">Occasion</label>
                      <select name="occasion" class="mjs-hint-select" required>
                        <option value="">Select occasion</option>
                        ${CONFIG.occasions.map(o => `<option value="${o}">${o}</option>`).join('')}
                      </select>
                    </div>
                    <div class="mjs-hint-form-field">
                      <label class="mjs-hint-form-label">Relationship</label>
                      <select name="relationship" class="mjs-hint-select" required>
                        <option value="">Select relationship</option>
                        ${CONFIG.relationships.map(r => `<option value="${r}">${r}</option>`).join('')}
                      </select>
                    </div>
                  </div>
                </div>

                <div class="mjs-hint-form-section">
                  <label class="mjs-hint-form-label">Personal Message <span class="mjs-hint-optional">(optional)</span></label>
                  <textarea name="message" placeholder="Add a personal touch... e.g., 'I've been eyeing this beautiful piece and thought you should know!'" class="mjs-hint-textarea" rows="3" maxlength="500"></textarea>
                  <div class="mjs-hint-char-count"><span id="mjs-hint-chars">0</span>/500</div>
                </div>

                <div class="mjs-hint-form-section mjs-hint-extras">
                  <label class="mjs-hint-checkbox-label">
                    <input type="checkbox" name="giftWrapping" class="mjs-hint-checkbox">
                    <span class="mjs-hint-checkbox-custom"></span>
                    <span>Suggest complimentary gift wrapping</span>
                  </label>
                  <label class="mjs-hint-checkbox-label">
                    <input type="checkbox" name="anonymous" class="mjs-hint-checkbox">
                    <span class="mjs-hint-checkbox-custom"></span>
                    <span>Send anonymously (hide my name)</span>
                  </label>
                </div>

                <button type="submit" class="mjs-hint-submit" id="mjs-hint-submit-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  Send Hint
                </button>

                <p class="mjs-hint-privacy">Your information is safe with us. We'll only use it to send this hint.</p>
              </form>

              <!-- Success State -->
              <div id="mjs-hint-success" class="mjs-hint-success" style="display:none;">
                <div class="mjs-hint-success-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#C9A86A" stroke-width="1.5">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </div>
                <h3>Hint Sent Successfully!</h3>
                <p>A beautifully crafted hint has been sent to <strong id="mjs-hint-recipient-name"></strong>.</p>
                <p class="mjs-hint-success-sub">They'll receive a personalised email with your selected piece${this.isMultiHint ? 's' : ''} and your message.</p>
                <div class="mjs-hint-success-actions">
                  <button class="mjs-hint-btn-secondary" id="mjs-hint-send-another">Send Another Hint</button>
                  <button class="mjs-hint-btn-primary" id="mjs-hint-continue-shopping">Continue Shopping</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(modal);
      this.bindEvents();
    },

    // Render single product preview
    renderSingleProductPreview() {
      const p = this.currentProduct;
      return `
        <div class="mjs-hint-product-preview">
          <div class="mjs-hint-product-card">
            <img src="${p.image}" alt="${p.title}" class="mjs-hint-product-img">
            <div class="mjs-hint-product-info">
              <h4 class="mjs-hint-product-name">${p.title}</h4>
              <p class="mjs-hint-product-price">${p.price}</p>
              ${p.vendor ? `<p class="mjs-hint-product-vendor">${p.vendor}</p>` : ''}
            </div>
          </div>
        </div>
      `;
    },

    // Render multi-product preview (from wishlist)
    renderMultiProductPreview() {
      return `
        <div class="mjs-hint-product-preview mjs-hint-multi">
          <p class="mjs-hint-multi-label">Hinting ${this.currentProducts.length} item${this.currentProducts.length > 1 ? 's' : ''} from your wishlist:</p>
          <div class="mjs-hint-product-grid">
            ${this.currentProducts.slice(0, 6).map(p => `
              <div class="mjs-hint-mini-card">
                <img src="${p.image}" alt="${p.title}" class="mjs-hint-mini-img">
                <span class="mjs-hint-mini-price">${p.price}</span>
              </div>
            `).join('')}
            ${this.currentProducts.length > 6 ? `<div class="mjs-hint-mini-more">+${this.currentProducts.length - 6} more</div>` : ''}
          </div>
        </div>
      `;
    },

    // Bind modal events
    bindEvents() {
      const modal = document.getElementById('mjs-hint-modal');
      const form = document.getElementById('mjs-hint-form');
      const closeBtn = modal.querySelector('.mjs-hint-close');
      const textarea = modal.querySelector('textarea[name="message"]');
      const charCount = document.getElementById('mjs-hint-chars');

      // Close modal
      closeBtn.addEventListener('click', () => this.hide());
      modal.addEventListener('click', (e) => {
        if (e.target === modal) this.hide();
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.hide();
      });

      // Character count
      if (textarea && charCount) {
        textarea.addEventListener('input', () => {
          charCount.textContent = textarea.value.length;
        });
      }

      // Form submit
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit(form);
      });

      // Success state buttons
      const sendAnother = document.getElementById('mjs-hint-send-another');
      const continueShopping = document.getElementById('mjs-hint-continue-shopping');

      if (sendAnother) {
        sendAnother.addEventListener('click', () => {
          document.getElementById('mjs-hint-success').style.display = 'none';
          form.style.display = 'block';
          form.reset();
          if (charCount) charCount.textContent = '0';
        });
      }

      if (continueShopping) {
        continueShopping.addEventListener('click', () => this.hide());
      }
    },

    // Handle form submission
    async handleSubmit(form) {
      const submitBtn = document.getElementById('mjs-hint-submit-btn');
      const formData = new FormData(form);

      // Validate
      if (!HintManager.canSendHint()) {
        this.showError('You\'ve reached the daily hint limit. Please try again tomorrow.');
        return;
      }

      // Disable button and show loading
      submitBtn.disabled = true;
      submitBtn.innerHTML = `
        <span class="mjs-hint-spinner"></span>
        Sending your hint...
      `;

      // Build hint data
      const hintData = {
        recipientName: formData.get('recipientName'),
        recipientEmail: formData.get('recipientEmail'),
        senderName: formData.get('senderName'),
        senderEmail: formData.get('senderEmail'),
        occasion: formData.get('occasion'),
        relationship: formData.get('relationship'),
        message: formData.get('message') || '',
        giftWrapping: formData.get('giftWrapping') === 'on',
        anonymous: formData.get('anonymous') === 'on',
        products: this.currentProducts.map(p => ({
          id: p.id,
          title: p.title,
          price: p.price,
          image: p.image,
          url: p.url,
          vendor: p.vendor || ''
        })),
        productTitle: this.currentProduct.title,
        productUrl: this.currentProduct.url,
        productPrice: this.currentProduct.price,
        productImage: this.currentProduct.image,
        source: this.isMultiHint ? 'wishlist' : 'product_page',
        isMultiHint: this.isMultiHint
      };

      try {
        // Save locally first so the hint has a durable ID before any backend calls
        const savedHint = HintManager.saveHint(hintData);
        hintData.id = savedHint.id;

        // Klaviyo is the required delivery path for Drop a Hint emails
        await KlaviyoIntegration.trackHintSent(hintData);
        if (this.isMultiHint) {
          KlaviyoIntegration.trackWishlistHint(this.currentProducts);
        }

        // Shopify contact submissions are best-effort only because storefront CAPTCHA can block them
        try {
          await this.sendHintEmail(hintData);
        } catch (contactErr) {
          console.warn('[MJS Hint] Shopify contact submission skipped:', contactErr);
        }

        // Show success only after the Klaviyo delivery path has completed
        this.showSuccess(hintData);

      } catch(err) {
        console.error('Hint send error:', err);
        this.showError('Something went wrong. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          Send Hint
        `;
      }
    },

    // Send hint via Shopify contact form endpoint
    async sendHintEmail(hintData) {
      const senderDisplay = hintData.anonymous ? 'Someone special' : hintData.senderName;
      const productList = hintData.products.map(p => 
        `- ${p.title} (${p.price}) - ${CONFIG.storeUrl}${p.url}`
      ).join('\n');

      const body = new FormData();
      body.append('form_type', 'contact');
      body.append('utf8', '\u2713');
      body.append('contact[email]', hintData.senderEmail);
      body.append('contact[name]', hintData.senderName);
      body.append('contact[subject]', `[DROP A HINT] ${senderDisplay} hinted at: ${hintData.productTitle}`);
      body.append('contact[body]', `
=== DROP A HINT ===

From: ${senderDisplay} (${hintData.senderEmail})
To: ${hintData.recipientName} (${hintData.recipientEmail})
Occasion: ${hintData.occasion}
Relationship: ${hintData.relationship}
Gift Wrapping: ${hintData.giftWrapping ? 'Yes' : 'No'}
Anonymous: ${hintData.anonymous ? 'Yes' : 'No'}

Personal Message:
${hintData.message || '(No message)'}

Product(s) Hinted:
${productList}

---
This hint was sent via the MJS Drop a Hint system.
Hint ID: ${hintData.id || 'pending'}
Source: ${hintData.source}
      `.trim());
      body.append('contact[tags]', 'drop-a-hint');

      const response = await fetch('/contact#contact_form', {
        method: 'POST',
        body: body
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown Shopify contact error');
        throw new Error(`Shopify contact form failed (${response.status}): ${errorText}`);
      }

      return response;
    },

    // Show success state
    showSuccess(hintData) {
      const form = document.getElementById('mjs-hint-form');
      const success = document.getElementById('mjs-hint-success');
      const recipientName = document.getElementById('mjs-hint-recipient-name');

      form.style.display = 'none';
      recipientName.textContent = hintData.recipientName;
      success.style.display = 'flex';
    },

    // Show error message
    showError(message) {
      const existing = document.querySelector('.mjs-hint-error');
      if (existing) existing.remove();

      const error = document.createElement('div');
      error.className = 'mjs-hint-error';
      error.textContent = message;
      
      const form = document.getElementById('mjs-hint-form');
      form.insertBefore(error, form.firstChild);

      setTimeout(() => error.remove(), 5000);
    },

    // Show modal
    show() {
      const modal = document.getElementById('mjs-hint-modal');
      if (modal) {
        // Ensure modal is direct child of body for proper stacking
        if (modal.parentElement !== document.body) {
          document.body.appendChild(modal);
        }
        // Force all parent elements to not create stacking context issues
        let parent = modal.parentElement;
        while (parent && parent !== document.documentElement) {
          parent.style.position = parent.style.position || 'static';
          parent = parent.parentElement;
        }
        document.body.style.overflow = 'hidden';
        // Double RAF to ensure browser has painted the display:flex before adding active
        modal.style.display = 'flex';
        modal.style.position = 'fixed';
        modal.style.top = '0';
        modal.style.left = '0';
        modal.style.width = '100vw';
        modal.style.height = '100vh';
        modal.style.zIndex = '2147483647';
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            modal.classList.add('active');
            modal.style.opacity = '1';
            modal.style.visibility = 'visible';
            modal.style.pointerEvents = 'auto';
            modal.style.background = 'rgba(0,0,0,0.65)';
          });
        });
      }
    },

    // Hide modal
    hide() {
      const modal = document.getElementById('mjs-hint-modal');
      if (modal) {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        setTimeout(() => modal.remove(), 300);
      }
    }
  };

  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {
    // Bind all "Drop a Hint" buttons on the page
    document.addEventListener('click', function(e) {
      const hintBtn = e.target.closest('[data-hint-trigger]');
      if (!hintBtn) return;

      e.preventDefault();

      const productData = {
        id: hintBtn.dataset.productId || '',
        title: hintBtn.dataset.productTitle || '',
        price: hintBtn.dataset.productPrice || '',
        image: hintBtn.dataset.productImage || '',
        url: hintBtn.dataset.productUrl || '',
        vendor: hintBtn.dataset.productVendor || '',
        source: hintBtn.dataset.hintSource || 'product_page'
      };

      HintModal.open(productData);
    });

    // Bind wishlist hint buttons (hint all wishlist items)
    document.addEventListener('click', function(e) {
      const wishlistHintBtn = e.target.closest('[data-hint-wishlist]');
      if (!wishlistHintBtn) return;

      e.preventDefault();

      // Get wishlist items from MJS Wishlist system
      let wishlistItems = [];
      try {
        wishlistItems = JSON.parse(localStorage.getItem('mjs_wishlist') || '[]');
      } catch(err) {
        wishlistItems = [];
      }

      if (wishlistItems.length === 0) {
        alert('Your wishlist is empty. Add some items first!');
        return;
      }

      const products = wishlistItems.map(item => ({
        id: item.id,
        title: item.title,
        price: item.price ? ('$' + parseFloat(item.price).toFixed(2)) : '',
        image: item.image,
        url: item.url,
        vendor: item.vendor || ''
      }));

      HintModal.openMulti(products);
    });

    // Bind individual wishlist item hint buttons
    document.addEventListener('click', function(e) {
      const itemHintBtn = e.target.closest('[data-hint-wishlist-item]');
      if (!itemHintBtn) return;

      e.preventDefault();

      const productData = {
        id: itemHintBtn.dataset.productId || '',
        title: itemHintBtn.dataset.productTitle || '',
        price: itemHintBtn.dataset.productPrice || '',
        image: itemHintBtn.dataset.productImage || '',
        url: itemHintBtn.dataset.productUrl || '',
        vendor: itemHintBtn.dataset.productVendor || '',
        source: 'wishlist_item'
      };

      HintModal.open(productData);
    });
  }

  // Expose globally for integration
  window.MJSHint = {
    open: (productData) => HintModal.open(productData),
    openMulti: (products) => HintModal.openMulti(products),
    getAnalytics: () => HintManager.getAnalytics(),
    canSend: () => HintManager.canSendHint(),
    todayCount: () => HintManager.getTodayCount()
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
